import React, { useRef, useEffect } from 'react';
import { Typography } from '@unfold/typography';
import { Button } from '@unfold/button';
import { ButtonsContainer, Spinner } from './styled';
import { Colors } from '@unfold/theme';
import { useBrandKitState } from '../../../store';
import { IFile } from '../../../common/types';

const TERMS_URL =
  'https://www.squarespace.com/terms-of-service/?_ga=2.85969817.1068062207.1602193673-677886448.1591388478';

const ImportExportModal: React.FC = () => {
  const [state, actions] = useBrandKitState();
  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Effect for exporting
  useEffect((): void => {
    if (state.exportURL !== '' && state.exporting) {
      actions.exportBrandKit(state.exportURL);
    }
  }, [state.exportURL, state.exporting]);

  // Effect for downloading the brand kit
  useEffect((): void => {
    if (state.downloadURL !== '') {
      const downloadAnchorNode: HTMLAnchorElement | null =
        downloadAnchorRef.current;
      downloadAnchorNode?.setAttribute('href', state.downloadURL);
      downloadAnchorNode?.setAttribute('download', 'brand.kit');
      downloadAnchorNode?.click();
      actions.finishExport();
    }
  }, [state.downloadURL]);

  const handleExportClick = async (): Promise<void> => {
    actions.getExportURL();
  };

  const handleImportClick = (): void => {
    inputRef.current?.click();
    actions.getImportBrandKitUrl();
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = e.target?.files?.item(0) as IFile;
    actions.importBrandKit(file, state.importURL);
  };

  return (
    <>
      <Typography variant="Paragraph" fontSize={14}>
        Import a brand kit or export yours to share it with other Unfold Pro users.
      </Typography>
      {state.error && (
        <Typography
          variant="Paragraph"
          fontSize={12}
          customColor={{ light: Colors.R500, dark: Colors.R200 }}
        >
          {state.error}
        </Typography>
      )}
      <ButtonsContainer>
        <Button
          appearance="bordered"
          width={132}
          fontWeight={500}
          onClick={handleImportClick}
        >
          {state.importing ? <Spinner /> : 'Import'}
        </Button>
        <input
          type="file"
          style={{ display: 'none' }}
          ref={inputRef}
          onChange={handleFileInputChange}
          multiple={false}
        />
        <Button
          appearance="bordered"
          width={132}
          fontWeight={500}
          onClick={handleExportClick}
        >
          {state.exporting ? <Spinner /> : 'Export'}
        </Button>
        <a style={{ display: 'none' }} ref={downloadAnchorRef} />
      </ButtonsContainer>
      <Typography
        variant="Paragraph"
        fontSize={12}
        lineHeight={15}
        customColor={{ light: Colors.G200, dark: Colors.G600 }}
      >
        By uploading any files, you must have the valid right to use such files
        for commercial purposes on social media, the web and any other use you
        will make of the file. By clicking Import or Export, you acknowledge
        that you have read and accepted our{' '}
        <a style={{ textDecoration: 'underline' }} href={TERMS_URL}>Terms of Service</a>.
      </Typography>
    </>
  );
};

export default ImportExportModal;
